import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRightIcon, CalendarIcon, CheckCircle2Icon, ClockIcon, MailIcon, MessageSquareIcon, PhoneIcon, UserIcon, BoxIcon } from "lucide-react";
interface ContactStep {
  icon: BoxIcon;
  title: string;
  description: string;
}
const contactSteps: ContactStep[] = [{
  icon: ClockIcon,
  title: 'Revisamos tu mensaje',
  description: 'Leemos tu contexto y detectamos los puntos que conviene abordar primero.'
}, {
  icon: MessageSquareIcon,
  title: 'Definimos la conversación',
  description: 'Te respondemos para entender prioridades, materiales y alcance.'
}, {
  icon: CalendarIcon,
  title: 'Aterrizamos el siguiente paso',
  description: 'Si hay encaje, acordamos la forma más clara de avanzar.'
}];
const sectors = [{
  value: 'hosteleria',
  label: 'Hostelería'
}, {
  value: 'servicios',
  label: 'Servicios locales'
}, {
  value: 'comercio',
  label: 'Comercio'
}, {
  value: 'profesional',
  label: 'Marca personal o profesional'
}, {
  value: 'otro',
  label: 'Otro'
}];
/** Campos homogéneos: misma altura, mismo borde, mismo estado de foco. */
const FIELD_CLASS = 'h-[52px] w-full border border-white/20 bg-black px-4 text-sm text-white transition-colors placeholder:text-neutral-500 hover:border-white/35 focus:border-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]';
const LABEL_CLASS = 'mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-300';
export function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    business: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFormStatus('submitting');
    window.setTimeout(() => {
      setFormStatus('success');
      window.setTimeout(() => setFormStatus('idle'), 4000);
    }, 900);
  };
  const updateField = (field: keyof typeof formData, value: string) => setFormData(current => ({
    ...current,
    [field]: value
  }));
  return <div className="min-h-screen bg-[#0a0a0a] pt-20 sm:pt-24" data-id="element-282" data-mp="2xvofiw28of5y">
      <section className="page-header" data-id="element-283" data-mp="31vffnqdno7n5">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-284" data-mp="3pkhtso92zicw">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.28,
          ease: [0.23, 1, 0.32, 1]
        }} className="grid gap-7 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-285" data-mp="3pl9qk2eezm4r">
            <div data-id="element-286" data-mp="3ks86zh39iyeu">
              <p className="eyebrow" data-id="element-287" data-mp="3krou4esgz07o">Contacto</p>
              <h1 className="mt-5 text-4xl font-medium tracking-[-0.055em] text-white sm:text-5xl lg:text-6xl" data-id="element-288" data-mp="cdpzphl5bmwa">
                Cuéntanos qué necesita tu negocio ahora.
              </h1>
            </div>
            <p className="max-w-2xl text-base leading-relaxed text-neutral-300 sm:text-lg" data-id="element-289" data-mp="21u1ytm8rsw58">
              Completa el formulario y utilizaremos la información para entender
              el punto de partida de tu proyecto. Sin mensajes automatizados ni
              compromisos innecesarios.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-shell" data-id="element-290" data-mp="1iw98rkj8wgp9">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-291" data-mp="1ix0qpsjx6fx2">
          <div className="grid overflow-hidden border border-white/10 lg:grid-cols-[0.85fr_1.15fr]" data-id="element-292" data-mp="1dxo51b4sbew2">
            <aside className="flex flex-col bg-black p-7 sm:p-10 lg:border-r lg:border-white/10" data-id="element-293" data-mp="1suye6dtyg8om">
              <p className="eyebrow" data-id="element-294" data-mp="1nusv3m1r3ui5">Después de escribirnos</p>
              <h2 className="mt-5 text-2xl font-medium tracking-[-0.035em] text-white" data-id="element-295" data-mp="1nxf1vjgti5zb">
                Una primera conversación con contexto.
              </h2>
              <div className="mt-9 space-y-7" data-id="element-296" data-mp="2z9pkix1uklq1">
                {contactSteps.map(step => {
                const StepIcon = step.icon;
                return <div key={step.title} className="flex gap-4" data-id="element-297" data-mp="18vqd4o0sndd9">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/20 text-white" data-id="element-298" data-mp="1dt08sles0l9j">
                        <StepIcon size={16} aria-hidden="true" data-id="element-299" data-mp="1hw69zen17m95" />
                      </span>
                      <div data-id="element-300" data-mp="1mr23ykbtwjow">
                        <h3 className="text-sm font-medium text-white" data-id="element-301" data-mp="1rshpseltyu8c">
                          {step.title}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-neutral-300" data-id="element-302" data-mp="1wq1ey9k2grf2">
                          {step.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
              <div className="mt-10 border-t border-white/10 pt-6 lg:mt-auto" data-id="element-303" data-mp="2air3akn2i8fc">
                <p className="flex items-center gap-2 text-xs font-medium text-neutral-200" data-id="element-304" data-mp="2agmpnazd9v1v">
                  <CheckCircle2Icon size={15} className="shrink-0 text-white" aria-hidden="true" data-id="element-305" data-mp="2fi3o6zudp5fa" />
                  Tus datos se tratan de forma confidencial.
                </p>
              </div>
            </aside>

            <motion.form initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.28,
            delay: 0.06,
            ease: [0.23, 1, 0.32, 1]
          }} onSubmit={handleSubmit} className="bg-[#0a0a0a] p-7 sm:p-10" data-id="element-306" data-mp="2kp0ompzfc3pk">
              <div className="grid gap-5 md:grid-cols-2" data-id="element-307" data-mp="33cvwjt8cunar">
                <label className="block" data-id="element-308" data-mp="33fi4vv32l6u1">
                  <span className={LABEL_CLASS} data-id="element-309" data-mp="2ylnmqw344zx2">Nombre completo *</span>
                  <span className="relative block" data-id="element-310" data-mp="2uku34ewpe79z">
                    <UserIcon size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden="true" data-id="element-311" data-mp="2ukc6hh64pr1j" />
                    <input required value={formData.name} onChange={event => updateField('name', event.target.value)} className={`${FIELD_CLASS} pl-11`} placeholder="Tu nombre" autoComplete="name" data-id="element-312" data-mp="3mc9u0l5amg60" />
                  </span>
                </label>
                <label className="block" data-id="element-313" data-mp="1pd17kzq1s7w1">
                  <span className={LABEL_CLASS} data-id="element-314" data-mp="1aoo7bqjxg2vq">Negocio *</span>
                  <input required value={formData.business} onChange={event => updateField('business', event.target.value)} className={FIELD_CLASS} placeholder="Nombre de tu negocio" autoComplete="organization" data-id="element-315" data-mp="1aoeg5zn5bpog" />
                </label>
                <label className="block" data-id="element-316" data-mp="16n2wsbuv80op">
                  <span className={LABEL_CLASS} data-id="element-317" data-mp="16nukz1m4uw4y">Correo electrónico *</span>
                  <span className="relative block" data-id="element-318" data-mp="rqc0wckeh73u">
                    <MailIcon size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden="true" data-id="element-319" data-mp="rrwthwn5mfz2" />
                    <input required type="email" value={formData.email} onChange={event => updateField('email', event.target.value)} className={`${FIELD_CLASS} pl-11`} placeholder="tu@email.com" autoComplete="email" data-id="element-320" data-mp="2m3lcqb0f54p9" />
                  </span>
                </label>
                <label className="block" data-id="element-321" data-mp="aghsiusb61vc">
                  <span className={LABEL_CLASS} data-id="element-322" data-mp="ahj2sx1i2kc0">Teléfono *</span>
                  <span className="relative block" data-id="element-323" data-mp="p546as2aelkg">
                    <PhoneIcon size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden="true" data-id="element-324" data-mp="p6yr86dk6doj" />
                    <input required type="tel" value={formData.phone} onChange={event => updateField('phone', event.target.value)} className={`${FIELD_CLASS} pl-11`} placeholder="+34 600 000 000" autoComplete="tel" data-id="element-325" data-mp="y5sm9sb8qvzz" />
                  </span>
                </label>
              </div>
              <label className="mt-5 block" data-id="element-326" data-mp="1355jn6zqnlgt">
                <span className={LABEL_CLASS} data-id="element-327" data-mp="1366rz3pddtuu">Sector o actividad *</span>
                <select required value={formData.service} onChange={event => updateField('service', event.target.value)} className={`${FIELD_CLASS} appearance-none bg-[url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23a3a3a3' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")] bg-[length:16px] bg-[right_1rem_center] bg-no-repeat pr-11 ${formData.service ? 'text-white' : 'text-neutral-500'}`} data-id="element-328" data-mp="39v3aqrk4icib">
                  <option value="" disabled data-id="element-329" data-mp="2xs93mu75e62f">
                    Selecciona una opción
                  </option>
                  {sectors.map(sector => <option key={sector.value} value={sector.value} data-id="element-330" data-mp="205jve2luqx84">
                      {sector.label}
                    </option>)}
                </select>
              </label>
              <label className="mt-5 block" data-id="element-331" data-mp="2a251kr9lyedj">
                <span className={LABEL_CLASS} data-id="element-332" data-mp="2a13fzsuyu9hk">
                  Sobre el proyecto{' '}
                  <span className="normal-case tracking-normal text-neutral-400" data-id="element-333" data-mp="2f2j1tn4ywk10">
                    (opcional)
                  </span>
                </span>
                <textarea rows={5} value={formData.message} onChange={event => updateField('message', event.target.value)} className={`${FIELD_CLASS} h-auto resize-none py-3.5 leading-relaxed`} placeholder="Qué quieres mejorar, qué necesitas ordenar o qué objetivo tienes en mente." data-id="element-334" data-mp="3qgo8inwkf801" />
              </label>
              <button type="submit" disabled={formStatus !== 'idle'} className="mt-7 flex h-[52px] w-full items-center justify-center gap-2 bg-white px-5 text-sm font-semibold text-black transition-colors hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a] disabled:cursor-not-allowed disabled:opacity-70" data-id="element-335" data-mp="153827b0zs3xv">
                {formStatus === 'idle' && <>
                    Enviar consulta{' '}
                    <ArrowRightIcon size={16} aria-hidden="true" data-id="element-336" data-mp="h6c6bmnnxe8v" />
                  </>}
                {formStatus === 'submitting' && 'Enviando...'}
                {formStatus === 'success' && 'Consulta recibida'}
              </button>
              <p className="mt-4 text-center text-xs leading-relaxed text-neutral-400" data-id="element-337" data-mp="2bkd4s6favz8f">
                Al enviar, aceptas que utilicemos esta información para
                responder a tu consulta.
              </p>
            </motion.form>
          </div>
        </div>
      </section>
    </div>;
}